import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center justify-center overflow-hidden pt-16">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20minimalist%20lifestyle%20product%20display%20with%20warm%20sandy%20beige%20background%2C%20elegant%20fashion%20accessories%20and%20sneakers%20arranged%20on%20clean%20oak%20wooden%20surface%2C%20gentle%20diffused%20natural%20light%20from%20large%20window%2C%20airy%20spacious%20composition%20with%20cream%20and%20warm%20taupe%20tones%2C%20editorial%20catalog%20aesthetic%2C%20high%20end%20product%20photography%2C%20no%20text%2C%20serene%20shopping%20atmosphere&width=1800&height=1000&seq=hero-main-vigorbuy-10&orientation=landscape&nocache=true"
          alt="Vigorbuy Spreadsheet lifestyle shopping scene"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/20"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-16 md:py-20">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-6">
          <i className="ri-star-line text-white text-sm"></i>
          <span className="text-white/90 text-xs font-medium whitespace-nowrap">Updated 2026 Resources</span>
        </div>

        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight text-balance">
          Vigorbuy Spreadsheet 2026
        </h1>
        <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
          The ultimate resource for product discovery, shopping guides, and verified finds.
          Explore organized categories, expert recommendations, and everything you need to shop smarter.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-7 py-3 rounded-md bg-white text-foreground-950 font-semibold text-sm hover:bg-white/90 transition-all whitespace-nowrap"
          >
            Vigorbuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-7 py-3 rounded-md bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 text-white/70 text-xs">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Updated Resources
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Beginner Friendly
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Shopping Guides
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Safety Information
          </span>
        </div>

        <div className="relative mt-10 max-w-sm mx-auto" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-5 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium hover:bg-white/15 transition-all cursor-pointer"
          >
            <span className="flex items-center gap-2 whitespace-nowrap">
              <i className="ri-grid-line"></i> Browse Categories
            </span>
            {dropdownOpen ? <i className="ri-arrow-up-s-line transition-transform duration-200"></i> : <i className="ri-arrow-down-s-line transition-transform duration-200"></i>}
          </button>

          {dropdownOpen && (
            <div className="absolute top-full mt-2 left-0 w-full bg-white rounded-lg border border-background-200 overflow-hidden z-50 max-h-72 overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                  onClick={() => setDropdownOpen(false)}
                >
                  <div className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                  </div>
                  <span className="whitespace-nowrap">{cat.name}</span>
                  <i className="ri-arrow-right-up-line text-foreground-400 text-xs ml-auto"></i>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}